import React, { useMemo } from "react";
import { Button } from "flowbite-react";

const findCycleForPlan = (plan, interval) =>
  (plan?.cycles || []).find((cycle) => cycle.intervalMonths === interval);

const installmentTextForCycle = (cycle, formatCurrency) => {
  if (!cycle) return "";
  const installments = Number(cycle.intervalMonths);
  if (!Number.isFinite(installments) || installments <= 1) {
    return formatCurrency ? formatCurrency(cycle.price) : `${cycle.price}`;
  }
  const installmentValue = cycle.price / installments;
  const formattedInstallment = formatCurrency
    ? formatCurrency(installmentValue)
    : `${installmentValue}`;
  return `${installments}x de ${formattedInstallment}`;
};

const PlanSelectionModal = ({
  isOpen,
  onClose,
  availablePlans = [],
  planModalSelection,
  setPlanModalSelection,
  selectedInterval,
  setSelectedInterval,
  expandedPlanId,
  setExpandedPlanId,
  onConfirm,
  confirmLabel = "Selecionar plano",
  disableBackdropClick = false,
  confirmDisabled = false,
  formatCurrency,
}) => {
  const cycleOptions = useMemo(() => {
    const monthsSet = new Set();
    availablePlans.forEach((plan) => {
      (plan.cycles || []).forEach((cycle) => {
        const monthValue = Number(cycle.intervalMonths);
        if (Number.isFinite(monthValue)) {
          monthsSet.add(monthValue);
        }
      });
    });
    return Array.from(monthsSet).sort((a, b) => a - b);
  }, [availablePlans]);

  const displayedIntervals =
    cycleOptions.length > 0 ? cycleOptions : [12, 3, 1];

  const handleCardClick = (plan) => {
    const cycle = findCycleForPlan(plan, selectedInterval);
    if (!cycle) return;
    setPlanModalSelection({
      planId: plan.id,
      cycleId: cycle.id,
    });
  };

  const handleConfirm = () => {
    if (!planModalSelection?.planId) return;
    const plan = availablePlans.find((p) => p.id === planModalSelection.planId);
    const cycle = plan
      ? (plan.cycles || []).find(
          (c) => c.id === planModalSelection.cycleId
        )
      : null;
    if (!plan || !cycle || confirmDisabled) return;
    onConfirm?.(plan, cycle);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center overflow-y-auto px-4 py-8"
      style={{
        background: "linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.3))",
        zIndex: "500",
        marginTop: 0,
      }}
    >
      <div
        className="absolute inset-0"
        onClick={() => {
          if (!disableBackdropClick) {
            onClose?.();
          }
        }}
      />
      <div className="relative z-10 w-full max-w-6xl rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="text-center">
          <h3 className="text-2xl font-semibold text-slate-900">
            Escolha o seu plano ideal
          </h3>
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-1 md:gap-3 rounded-full bg-gradient-to-r from-white/90 to-orange-50 px-4 py-3 shadow-inner shadow-orange-200/70">
          {displayedIntervals.map((months) => (
            <button
              key={months}
              type="button"
              onClick={() => setSelectedInterval(months)}
              className={`flex min-w-[110px] transform flex-col items-center justify-center rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] transition duration-300 focus-visible:outline-none ${
                selectedInterval === months
                  ? "border-[#f9b388] bg-[#e45412] text-white shadow-[0_20px_30px_rgba(228,84,18,0.35)]"
                  : "border border-white/70 bg-white/90 text-slate-600 text-[10px] hover:border-[#e45412] hover:text-[#e45412]"
              }`}
            >
              <span
                className={`text-xs md:text-sm font-semibold tracking-[0.2em] ${
                  selectedInterval === months ? "text-white" : "text-slate-800"
                }`}
              >
                {intervalLabel(months)}
              </span>
            </button>
          ))}
        </div>
        <div
          className="mt-8 grid gap-6 lg:grid-cols-3 overflow-y-scroll p-3"
          style={{ maxHeight: "600px" }}
        >
          {availablePlans.map((plan) => {
            const highlighted =
              planModalSelection.planId === plan.id &&
              planModalSelection.cycleId ===
                findCycleForPlan(plan, selectedInterval)?.id;
            const cycle = findCycleForPlan(plan, selectedInterval);
            return (
              <div
                key={plan.id}
                className={`flex h-full flex-col rounded-xl border border-transparent bg-gradient-to-b from-white/80 to-orange-50 p-4 shadow-md transition-all ${
                  highlighted
                    ? "border-[#e45412] bg-gradient-to-b from-[#fff7f0] to-[#ffe2ca] shadow-[0_30px_60px_rgba(228,84,18,0.25)]"
                    : "border-transparent"
                } ${cycle ? "cursor-pointer" : "opacity-70"}`}
                onClick={() => cycle && handleCardClick(plan)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-xl font-semibold text-slate-900">
                      {plan.name}
                    </p>
                    <p className="text-sm text-slate-500">
                      {plan.description}
                    </p>
                  </div>
                </div>
                <div className="mt-5 text-sm text-slate-600">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.4em] text-slate-500">
                    Funcionalidades
                  </p>
                  <div className="mt-2 space-y-2">
                    {((plan.features || []).length === 0
                      ? []
                      : plan.features)
                      .slice(
                        0,
                        expandedPlanId === plan.id
                          ? plan.features.length
                          : MAX_FEATURES_PREVIEW
                      )
                      .map((feature) => (
                        <div
                          key={`${plan.id}-${feature}`}
                          className="flex items-start gap-2"
                        >
                          <span className="mt-[2px] h-2 w-2 rounded-full bg-[#e45412]" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    {plan.features?.length > MAX_FEATURES_PREVIEW && (
                      <button
                        type="button"
                        className="text-xs font-semibold uppercase tracking-[0.4em] text-[#e45412]"
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedPlanId((current) =>
                            current === plan.id ? null : plan.id
                          );
                        }}
                      >
                        {expandedPlanId === plan.id
                          ? "Mostrar menos"
                          : `+${
                              plan.features.length - MAX_FEATURES_PREVIEW
                            } funcionalidades`}
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex flex-col gap-1 mb-4">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Investimento
                  </p>
                  <p className="text-2xl font-semibold text-slate-900">
                    {cycle ? installmentTextForCycle(cycle, formatCurrency) : "—"}
                  </p>
                  {cycle?.intervalMonths === 12 && (
                    <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-emerald-600">
                      Plano anual com mais desconto
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  className="mt-4 self-stretch"
                  style={{ marginTop: "auto" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleConfirm();
                  }}
                  disabled={!cycle || confirmDisabled}
                >
                  {confirmLabel}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const intervalLabel = (months) => {
  if (!months || months <= 0) return "-";
  if (months === 1) return "Mensal";
  if (months === 3) return "Trimestral";
  if (months === 12) return "Anual";
  return `${months} meses`;
};

const MAX_FEATURES_PREVIEW = 6;

export default PlanSelectionModal;
